import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSchool } from '../contexts/SchoolContext';
import ApplicationForm from '../components/ApplicationForm';
import './Admissions.css';

const Admissions = () => {
  const { settings, eligibleGrades } = useSchool();
  const [admissionStatus, setAdmissionStatus] = useState(null);
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    fetchAdmissionStatus();
  }, []);

  useEffect(() => {
    if (admissionStatus?.remaining_ms && admissionStatus.show_countdown) {
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const target = admissionStatus.status === 'upcoming'
          ? new Date(admissionStatus.start_date).getTime()
          : new Date(admissionStatus.end_date).getTime();
        const diff = target - now;

        if (diff > 0) {
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          setCountdown({ days, hours, minutes, seconds });
        } else {
          fetchAdmissionStatus(); // Refresh status when time expires
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [admissionStatus]);

  const fetchAdmissionStatus = async () => {
    try {
      const { data, error } = await supabase.rpc('get_admission_status');
      if (error) throw error;
      setAdmissionStatus(data);
    } catch (error) {
      console.error('Error fetching admission status:', error);
      // Fail open - allow submissions if status check fails
      setAdmissionStatus({ is_open: true, status: 'open' });
    }
  };

  return (
    <div className="admissions">
      <section className="admissions-hero page-hero">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Admissions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Join our school community — Start your application today
          </motion.p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          {/* Admission Status Banner */}
          {admissionStatus && (
            <div className={`admission-status-banner status-${admissionStatus.status}`}>
              <div className="status-content">
                <div className="status-header">
                  <span className="status-badge">
                    {admissionStatus.status === 'open' && '✓ Admissions Open'}
                    {admissionStatus.status === 'upcoming' && '⏳ Admissions Opening Soon'}
                    {admissionStatus.status === 'closed' && '⚠ Admissions Closed'}
                  </span>
                  {admissionStatus.academic_year && (
                    <span className="academic-year"> Academic year {admissionStatus.academic_year}</span>
                  )}
                </div>

                {admissionStatus.announcement_message && (
                  <p className="announcement-message">{admissionStatus.announcement_message}</p>
                )}

                {/* Countdown Timer */}
                {admissionStatus.show_countdown && countdown && 
                 admissionStatus.status !== 'closed' && 
                 (countdown.days > 0 || countdown.hours > 0 || countdown.minutes > 0 || countdown.seconds > 0) ? (
                  <div className="countdown-timer">
                    <h3>
                      {admissionStatus.status === 'upcoming'
                        ? 'Time Until Applications Open:'
                        : 'Time Remaining:'}
                    </h3>
                    <div className="countdown-display">
                      <div className="countdown-item">
                        <span className="countdown-value">{countdown.days}</span>
                        <span className="countdown-label">Days</span>
                      </div>
                      <div className="countdown-item">
                        <span className="countdown-value">{countdown.hours}</span>
                        <span className="countdown-label">Hours</span>
                      </div>
                      <div className="countdown-item">
                        <span className="countdown-value">{countdown.minutes}</span>
                        <span className="countdown-label">Minutes</span>
                      </div>
                      <div className="countdown-item">
                        <span className="countdown-value">{countdown.seconds}</span>
                        <span className="countdown-label">Seconds</span>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Date Information */}
                {admissionStatus.status === 'upcoming' && admissionStatus.start_date && (
                  <p className="date-info">
                    Applications will open on{' '}
                    {new Date(admissionStatus.start_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
                {admissionStatus.status === 'open' && admissionStatus.end_date && (
                  <p className="date-info">
                    Applications close on{' '}
                    {new Date(admissionStatus.end_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="admissions-content">
            {admissionStatus?.is_open ? (
              <div className="admission-info">
                <h2>Admission Process</h2>
                <ol>
                  <li>
                    <strong>Submit Application:</strong> Complete the online application form below
                  </li>
                  <li>
                    <strong>Application Review:</strong> Our admissions team will review your application
                  </li>
                  <li>
                    <strong>Schedule Visit:</strong> Schedule a campus tour and meet with our staff
                  </li>
                  <li>
                    <strong>Enrollment:</strong> Once accepted, complete enrollment paperwork and payment
                  </li>
                </ol>

                <h3>Requirements</h3>
                <ul>
                  <li>Completed application form</li>
                  <li>Birth certificate copy</li>
                  <li>Immunization records</li>
                  <li>Previous school records (if applicable)</li>
                  <li>Parent/Guardian ID</li>
                </ul>

                {eligibleGrades.length > 0 && (
                  <>
                    <h3>Programs Open for Admission</h3>
                    <p className="eligible-grades">
                      {settings.academic_year ? `Academic year ${settings.academic_year}: ` : ''}
                      {eligibleGrades.map((g) => g.label).join(', ')}
                    </p>
                  </>
                )}

                {settings.faqs?.length > 0 && (
                  <p className="faq-link">
                    Have a question? See our <Link to="/faq">frequently asked questions</Link>.
                  </p>
                )}

                <h3>Tuition Information</h3>
                <p>
                  For detailed tuition rates and payment plans, please contact our admissions office 
                  at <a href={`mailto:${settings.email_admissions}`}>{settings.email_admissions}</a> or call{' '}
                  {settings.phones.map((phone, index) => (
                    <React.Fragment key={phone.tel}>
                      {index > 0 && ' | '}
                      <a href={`tel:${phone.tel}`}>{phone.display}</a>
                    </React.Fragment>
                  ))}
                  , and you can contact us for more details.
                </p>
              </div>
            ) : null}

            {admissionStatus && !admissionStatus.is_open ? (
              <div className="contact-cta-full">
                <h2>Questions About Admissions?</h2>
                <p className="subtitle">
                  Our admissions team is here to help answer your questions and guide you through the process.
                </p>
                <div className="contact-methods">
                  <div className="contact-item">
                    <strong>📧 Email:</strong>
                    <a href={`mailto:${settings.email_admissions}`}>{settings.email_admissions}</a>
                  </div>
                  <div className="contact-item">
                    <strong>📞 Phone:</strong>
                    {settings.phones.length > 0 && (
                      <a href={`tel:${settings.phones[0].tel}`}>{settings.phones[0].display}</a>
                    )}
                  </div>
                </div>
                <p className="cta-note">
                  {admissionStatus.status === 'upcoming'
                    ? 'Check back soon when applications open to submit your application online.'
                    : 'Please contact us for information about the next admission period.'}
                </p>
              </div>
            ) : null}

            {admissionStatus?.is_open ? <ApplicationForm /> : null}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Admissions;
